/**
 * Product feedback for the in-app dock.
 * Prefers a PostHog API survey with one open-text question so responses show in Surveys.
 * Otherwise captures PRODUCT_FEEDBACK_EVENT, which still lands on the person in PostHog.
 */
import { capture, messageLengthBucket, PRODUCT_FEEDBACK_EVENT } from './posthog';

const SURVEY_SHOWN_EVENT = 'survey shown';
const SURVEY_SENT_EVENT = 'survey sent';
const OPEN_QUESTION_TYPES = new Set(['open', 'open_text']);

function posthogReady(client) {
  return Boolean(client && client.__loaded === true && typeof client.capture === 'function');
}

function isOpenFeedbackSurvey(survey) {
  if (!survey || survey.type !== 'api') return false;
  const questions = survey.questions;
  if (!Array.isArray(questions) || questions.length !== 1) return false;
  const question = questions[0];
  return Boolean(
    question &&
    typeof question.id === 'string' &&
    question.id &&
    OPEN_QUESTION_TYPES.has(question.type)
  );
}

/**
 * First active API survey the dock can submit as a single open-text response.
 * @param {unknown} surveys
 */
export function openFeedbackSurvey(surveys) {
  if (!Array.isArray(surveys)) return null;
  return surveys.find(isOpenFeedbackSurvey) ?? null;
}

function surveyProps(survey) {
  const props = { $survey_id: survey.id };
  if (survey.current_iteration != null) props.$survey_iteration = survey.current_iteration;
  return props;
}

function submissionId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `fb_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Load the open-text survey once feature flags and surveys are ready.
 * @param {import('posthog-js').PostHog | null | undefined} client
 * @param {(survey: object | null) => void} onSurvey
 * @returns {() => void}
 */
export function subscribeOpenFeedbackSurvey(client, onSurvey) {
  if (!posthogReady(client) || typeof client.getActiveMatchingSurveys !== 'function') {
    onSurvey(null);
    return () => {};
  }

  let cancelled = false;
  const read = () => {
    client.getActiveMatchingSurveys((surveys) => {
      if (!cancelled) onSurvey(openFeedbackSurvey(surveys));
    });
  };

  const unsubscribers = [];
  if (typeof client.onFeatureFlags === 'function') {
    const off = client.onFeatureFlags(read);
    if (typeof off === 'function') unsubscribers.push(off);
  }
  if (typeof client.onSurveysLoaded === 'function') {
    const off = client.onSurveysLoaded(read);
    if (typeof off === 'function') unsubscribers.push(off);
  }
  if (unsubscribers.length === 0) read();

  return () => {
    cancelled = true;
    unsubscribers.forEach((off) => off());
  };
}

export function captureFeedbackSurveyShown(client, survey, source) {
  if (!posthogReady(client) || !isOpenFeedbackSurvey(survey)) return;
  capture(client, SURVEY_SHOWN_EVENT, { ...surveyProps(survey), source });
}

/**
 * @returns {boolean} false when PostHog cannot accept the note
 */
export function sendProductFeedback(client, { message, source, survey }) {
  const feedback = typeof message === 'string' ? message.trim() : '';
  if (!feedback || !posthogReady(client)) return false;

  if (isOpenFeedbackSurvey(survey)) {
    const question = survey.questions[0];
    capture(client, SURVEY_SENT_EVENT, {
      ...surveyProps(survey),
      $survey_submission_id: submissionId(),
      $survey_completed: true,
      $survey_questions: [{ id: question.id, question: question.question }],
      [`$survey_response_${question.id}`]: feedback,
      source,
    });
    return true;
  }

  capture(client, PRODUCT_FEEDBACK_EVENT, {
    feedback,
    source,
    message_length_bucket: messageLengthBucket(feedback.length),
  });
  return true;
}
